// src/components/Teabates/TeabateCarousel.tsx
import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Plus } from 'lucide-react';
import api from 'config/axiosConfig';
import TeabateStoryContainer from './TeabateStoryContainer';
import IssueTeabateAvatar from './IssueTeabateAvatar';
import SpillTheTeaModal from 'components/Posts/SpillTheTeaModal';
import { LAYER_TOKENS } from 'ui/layers';
import { apiRoutes } from "util/apiRoutes";

import 'swiper/css';
import { Issue } from 'util/constants';

interface IssueWithTeabates extends Issue {
    teabateCount: number;
}

interface TeabateCarouselProps {
  onIssueClick?: (issueId: number) => void;
}

const TeabateCarousel: React.FC<TeabateCarouselProps> = ({ onIssueClick }) => {
  const [issues, setIssues] = useState<IssueWithTeabates[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [storyMode, setStoryMode] = useState<{
    active: boolean;
    issueId: number | null;
  }>({
    active: false,
    issueId: null,
  });
  
  useEffect(() => {
    fetchIssuesWithTeabates();
  }, []);
  
  useEffect(() => {
    // Handle body styling when story mode is active
    if (storyMode.active) {
      // Hide app bar and other navigation elements
      document.body.classList.add('teabate-story-active');
    } else {
      document.body.classList.remove('teabate-story-active');
    }
    
    return () => {
      document.body.classList.remove('teabate-story-active');
    };
  }, [storyMode.active]);
  
  const handleIssueClick = (issueId: number) => {
    setStoryMode({
      active: true,
      issueId,
    });
    if (onIssueClick) {
      onIssueClick(issueId);
    }
  };
  
  const handleStoryClose = () => {
    setStoryMode({
      active: false,
      issueId: null,
    });
  };

  const handleTeabateCreated = () => {
    fetchIssuesWithTeabates();
    setShowCreateModal(false);
  };
  
  const fetchIssuesWithTeabates = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(apiRoutes.posts.root, { params: { kind: "teabate" } });
      const teabates = response.data;
      
      const issueMap = new Map<number, number>();
      teabates.forEach((teabate: any) => {
        const count = issueMap.get(teabate.issue_id) || 0;
        issueMap.set(teabate.issue_id, count + 1);
      });
      
      const issueIds = Array.from(issueMap.keys());
      if (issueIds.length > 0) {
        const issuesResponse = await api.get(apiRoutes.issues.root, {
          params: { issueIds: JSON.stringify(issueIds) }
        });
        
        const issuesWithTeabates = issuesResponse.data.map((issue: any) => ({
          ...issue,
          teabateCount: issueMap.get(issue.id) || 0,
        }));
        
        const sortedIssues = [...issuesWithTeabates].sort((a, b) => b.teabateCount - a.teabateCount);
        setIssues(sortedIssues);
      } else {
        setIssues([]);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Error fetching issues with teabates:', err);
      setError('Failed to load teabates');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent border-eoe-primary dark:border-eoe-dark-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="px-4 py-2 text-warning-red dark:text-dark-warning-red text-sm">
        {error}
      </div>
    );
  }
  
  return (
    <div className="mb-4 mt-1">
      <div className="mb-2 px-4">
        <Swiper spaceBetween={12} slidesPerView="auto" className="pb-2">
          <SwiperSlide className="w-auto">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="flex cursor-pointer flex-col items-center"
              aria-label="Create a new teabate"
            >
              <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-eoe-primary dark:hover:border-eoe-dark-primary transition-colors">
                <Plus className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <span className="text-xs mt-1.5 font-medium text-gray-600 dark:text-gray-400">
                Teabate
              </span>
            </button>
          </SwiperSlide>
          {issues.map(issue => (
            <SwiperSlide key={issue.id} className="w-auto">
              <IssueTeabateAvatar 
                issue={issue}
                hasTeabates={issue.teabateCount > 0}
                onClick={() => handleIssueClick(issue.id)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {storyMode.active && storyMode.issueId && (
        <div
          className="fixed inset-0 h-screen w-screen bg-black"
          style={{ zIndex: LAYER_TOKENS.storySurface }}
          data-teabate-story-overlay="true"
        >
          <TeabateStoryContainer issueId={storyMode.issueId} onClose={handleStoryClose} />
        </div>
      )}
      <SpillTheTeaModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onImpactAdded={handleTeabateCreated}
        customTitle="Start a teabate"
        fixedPostType="teabate"
        hidePostTypeButtons={true}
      />
    </div>
  );
};

export default TeabateCarousel;
